<div class="h-full w-full flex flex-col justify-start items-center overflow-hidden space-y-2">

    @if (isLoadingInitialData()) {
    <atom-loading-inline title="Loading" message="Loading results..."></atom-loading-inline>
    } @else if (noContentForReporting()) {
    <div class="w-full h-full flex items-center justify-center overflow-y-auto px-4 py-8 sm:px-8">
        <div class="w-full max-w-3xl">
            <div
                class="rounded-2xl border border-zinc-200 bg-linear-to-b from-primary/5 to-transparent p-6 sm:p-10 dark:border-zinc-800 dark:from-primary/10">
                <div class="text-center">
                    <span
                        class="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-600/20 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20">
                        <span class="size-1.5 rounded-full bg-amber-500 animate-pulse" aria-hidden="true"></span>
                        Waiting for posts
                    </span>

                    <div class="relative mx-auto mt-6 flex size-20 items-center justify-center">
                        <div class="absolute inset-0 rounded-full bg-primary/10 dark:bg-primary/15" aria-hidden="true">
                        </div>
                        <div class="absolute inset-2 rounded-full bg-primary/5 dark:bg-primary/10" aria-hidden="true">
                        </div>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                            class="relative size-9 text-accent" aria-hidden="true">
                            <circle cx="7.5" cy="7.5" r=".5" fill="currentColor" />
                            <circle cx="18.5" cy="5.5" r=".5" fill="currentColor" />
                            <circle cx="11.5" cy="11.5" r=".5" fill="currentColor" />
                            <circle cx="7.5" cy="16.5" r=".5" fill="currentColor" />
                            <circle cx="17.5" cy="14.5" r=".5" fill="currentColor" />
                            <path d="M3 3v16a2 2 0 0 0 2 2h16" />
                        </svg>
                    </div>

                    <h2 class="mt-6 text-2xl font-semibold text-primary use-grotesque">Brief Reporting</h2>
                    <p class="mx-auto mt-3 max-w-lg text-sm leading-6 text-secondary sm:text-base">
                        Once creators publish content to social media, we analyse the results and build a full
                        performance report for this brief.
                    </p>
                </div>

                <p class="mt-8 text-center text-xs font-semibold uppercase tracking-wide text-secondary">
                    What you'll see here
                </p>

                <ul role="list" class="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
                    <li
                        class="flex gap-3 rounded-xl border border-zinc-200 bg-white/70 p-4 text-left dark:border-zinc-800 dark:bg-zinc-900/50">
                        <div
                            class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 dark:bg-primary/15">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" class="size-5 text-accent" aria-hidden="true">
                                <path
                                    d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719" />
                            </svg>
                        </div>
                        <div>
                            <h3 class="text-sm font-semibold text-primary">Comment Sentiment</h3>
                            <p class="mt-1 text-xs leading-5 text-secondary">
                                Understand what the audience thought of the content.
                            </p>
                        </div>
                    </li>
                    <li
                        class="flex gap-3 rounded-xl border border-zinc-200 bg-white/70 p-4 text-left dark:border-zinc-800 dark:bg-zinc-900/50">
                        <div
                            class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 dark:bg-primary/15">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" class="size-5 text-accent" aria-hidden="true">
                                <circle cx="12" cy="12" r="10" />
                                <circle cx="12" cy="12" r="6" />
                                <circle cx="12" cy="12" r="2" />
                            </svg>
                        </div>
                        <div>
                            <h3 class="text-sm font-semibold text-primary">Brief Accuracy</h3>
                            <p class="mt-1 text-xs leading-5 text-secondary">
                                How accurately creators achieved the main brief points.
                            </p>
                        </div>
                    </li>
                    <li
                        class="flex gap-3 rounded-xl border border-zinc-200 bg-white/70 p-4 text-left dark:border-zinc-800 dark:bg-zinc-900/50">
                        <div
                            class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 dark:bg-primary/15">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" class="size-5 text-accent" aria-hidden="true">
                                <path d="M16 7h6v6" />
                                <path d="m22 7-8.5 8.5-5-5L2 17" />
                            </svg>
                        </div>
                        <div>
                            <h3 class="text-sm font-semibold text-primary">Content Performance</h3>
                            <p class="mt-1 text-xs leading-5 text-secondary">
                                All social posts and their engagement metrics in one place.
                            </p>
                        </div>
                    </li>
                    <li
                        class="flex gap-3 rounded-xl border border-zinc-200 bg-white/70 p-4 text-left dark:border-zinc-800 dark:bg-zinc-900/50">
                        <div
                            class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 dark:bg-primary/15">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" class="size-5 text-accent" aria-hidden="true">
                                <path d="M10 14.66v1.626a2 2 0 0 1-.976 1.696A5 5 0 0 0 7 21.978" />
                                <path d="M14 14.66v1.626a2 2 0 0 0 .976 1.696A5 5 0 0 1 17 21.978" />
                                <path d="M18 9h1.5a1 1 0 0 0 0-5H18" />
                                <path d="M4 22h16" />
                                <path d="M6 9a6 6 0 0 0 12 0V3a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1z" />
                                <path d="M6 9H4.5a1 1 0 0 1 0-5H6" />
                            </svg>
                        </div>
                        <div>
                            <h3 class="text-sm font-semibold text-primary">Top Performers</h3>
                            <p class="mt-1 text-xs leading-5 text-secondary">
                                Which posts drove the most views, engagements, and likes.
                            </p>
                        </div>
                    </li>
                </ul>
            </div>
        </div>
    </div>
    } @else {


    @if (focussedBrief()) {

    <!-- Cross Platforms -->
    @if (showChannelTabs() || isPieceBrief()) {
    <div
        class="w-full rounded-md bg-fuchsia-50 px-4 py-2 dark:bg-fuchsia-500/10 dark:outline dark:outline-fuchsia-500/20">
        <div class="flex">
            <div class="w-full flex flex-row items-center justify-between">


                <div class="flex items-center">
                    <div class="shrink-0">
                        <div
                            class="size-12 rounded-full bg-fuchsia-10 dark:bg-fuchsia-800 dark:outline dark:-outline-offset-1 dark:outline-white/10 text-fuchsia-600 flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" class="size-8">
                                <circle cx="12" cy="12" r="10" />
                                <path d="M12 16v-4" />
                                <path d="M12 8h.01" />
                            </svg>
                        </div>
                    </div>
                    <div class="ml-4">
                        @if (isPieceBrief()) {
                        <h3 class="text-base font-semibold text-fuchsia-600">Pieces &amp; platforms</h3>
                        <p class="text-sm text-fuchsia-400">
                            Select a piece or platform to view filtered results
                        </p>
                        } @else {
                        <h3 class="text-base font-semibold text-fuchsia-600">Cross Platform</h3>
                        <p class="text-sm text-fuchsia-400">
                            Select a platform to view filtered results
                        </p>
                        }
                    </div>
                </div>

                <div class="flex flex-col items-end justify-center gap-2">
                    @if (isPieceBrief()) {
                    <nav aria-label="Pieces" class="flex flex-wrap justify-end gap-2">
                        <button type="button" [attr.aria-pressed]="selectedPiece() === null"
                            class="rounded-md px-3 py-1 text-sm font-medium cursor-pointer"
                            [class]="getPieceTabClass(null)" (click)="setSelectedPiece(null)">All pieces</button>
                        @for (label of pieceLabels(); track label) {
                        <button type="button" [attr.aria-pressed]="selectedPiece() === label"
                            class="rounded-md px-3 py-1 text-sm font-medium cursor-pointer"
                            [class]="getPieceTabClass(label)" (click)="setSelectedPiece(label)">{{ label }}</button>
                        }
                    </nav>
                    }
                    @if (showChannelTabs()) {
                    <nav aria-label="Platforms" class="flex space-x-2">
                        <a (click)="setSelectedChannel('All')"
                            class="rounded-md px-3 py-1 text-sm font-medium cursor-pointer flex flex-row items-center justify-center gap-x-2"
                            [class]="getPlatformTabClass('All')">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" class="size-4">
                                <path
                                    d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z" />
                            </svg>
                            All
                        </a>
                        @for (plt of platformFilterChannels(); track plt) {
                        <a (click)="setSelectedChannel(plt)"
                            class="rounded-md px-3 py-1 text-sm font-medium cursor-pointer flex flex-row items-center justify-center gap-x-2"
                            [class]="getPlatformTabClass(plt)">

                            @switch (getNumber(plt)) {
                            @case (SocialChannelEnum['TikTok Post']) {
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke-width="2"
                                role="img" xmlns="http://www.w3.org/2000/svg" class="size-4">
                                <title>TikTok icon</title>
                                <path
                                    d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
                            </svg>
                            }
                            @case (SocialChannelEnum['Instagram Reel']) {
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" class="size-4">
                                <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
                                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                                <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
                            </svg>
                            }
                            @case (SocialChannelEnum['Instagram Story']) {
                            <svg xmlns="http://www.w3.org/2000/svg" width="306.509" height="307.201"
                                viewBox="0 0 306.509 307.201" fill="currentColor" class="size-4" aria-hidden="true">
                                <title>Instagram Story icon</title>
                                <g id="noun-instagram-stories-682475" transform="translate(-4.483 -4.529)">
                                    <g id="Group_6" data-name="Group 6" transform="translate(4.99 5.03)">
                                        <g id="Group_1" data-name="Group 1" transform="translate(74.798 268.357)">
                                            <path id="Path_13" data-name="Path 13"
                                                d="M104.574,121.725a153.446,153.446,0,0,1-72.016-17.96,10.566,10.566,0,1,1,9.955-18.64A132.2,132.2,0,0,0,104.574,100.6a132.733,132.733,0,0,0,30.106-3.448,10.567,10.567,0,1,1,4.8,20.58A154.072,154.072,0,0,1,104.574,121.725Z"
                                                transform="translate(-26.967 -83.88)" />
                                        </g>
                                        <g id="Group_2" data-name="Group 2" transform="translate(3.312 176.254)">
                                            <path id="Path_14" data-name="Path 14"
                                                d="M57.041,147.719a10.516,10.516,0,0,1-7.263-2.9A152.61,152.61,0,0,1,6.263,69.877,10.567,10.567,0,1,1,26.8,64.9a131.512,131.512,0,0,0,37.509,64.586,10.566,10.566,0,0,1-7.27,18.235Z"
                                                transform="translate(-5.963 -56.818)" />
                                        </g>
                                        <g id="Group_3" data-name="Group 3" transform="translate(0 82.444)">
                                            <path id="Path_15" data-name="Path 15"
                                                d="M15.569,96.284q-.521,0-1.052-.051A10.563,10.563,0,0,1,5.042,84.678,152.008,152.008,0,0,1,18.526,35.366a10.566,10.566,0,1,1,19.158,8.92A130.837,130.837,0,0,0,26.071,86.757,10.567,10.567,0,0,1,15.569,96.284Z"
                                                transform="translate(-4.99 -29.254)" />
                                        </g>
                                        <g id="Group_4" data-name="Group 4" transform="translate(44.343 35.689)">
                                            <path id="Path_16" data-name="Path 16"
                                                d="M28.577,39.816a10.564,10.564,0,0,1-8.158-17.269q1.414-1.726,2.876-3.4A10.566,10.566,0,0,1,39.226,33.026c-.841.963-1.664,1.943-2.478,2.93A10.55,10.55,0,0,1,28.577,39.816Z"
                                                transform="translate(-18.019 -15.516)" />
                                        </g>
                                        <g id="Group_5" data-name="Group 5" transform="translate(141.834)">
                                            <path id="Path_17" data-name="Path 17"
                                                d="M130.353,291.081a10.566,10.566,0,0,1-5.439-19.631A132.755,132.755,0,0,0,189.2,158.135c0-72.768-59.2-131.97-131.97-131.97a10.568,10.568,0,0,1,0-21.135c84.421,0,153.1,68.68,153.1,153.1a153.978,153.978,0,0,1-74.565,131.445A10.472,10.472,0,0,1,130.353,291.081Z"
                                                transform="translate(-46.664 -5.03)" />
                                        </g>
                                        <path id="Path_18" data-name="Path 18"
                                            d="M171.174,89.821H110.638V29.285H89.506V89.821H28.97v21.132H89.506v60.536h21.132V110.953h60.536Z"
                                            transform="translate(52.644 53.264)" />
                                    </g>
                                </g>
                            </svg>
                            }
                            @case (SocialChannelEnum['YouTube Shorts']) {
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" class="size-4">
                                <path
                                    d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17" />
                                <path d="m10 15 5-3-5-3z" />
                            </svg>
                            }
                            @case (SocialChannelEnum['Snapchat Spotlight']) {
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke-width="2"
                                role="img" xmlns="http://www.w3.org/2000/svg" class="size-4">
                                <path
                                    d="M21.9756 17.1497C21.8884 16.8566 21.4712 16.6499 21.4712 16.6499C21.4317 16.6288 21.3965 16.6099 21.3674 16.5951C20.672 16.2535 20.0555 15.8423 19.5365 15.3762C19.1193 15.0008 18.7623 14.5875 18.4758 14.1489C18.125 13.6132 17.961 13.1662 17.8904 12.9237C17.851 12.7655 17.8572 12.7022 17.8904 12.62C17.9174 12.5504 17.9984 12.485 18.0357 12.4534C18.2703 12.2847 18.6481 12.0358 18.8806 11.884C19.0819 11.7512 19.2542 11.6373 19.3559 11.5656C19.6818 11.3336 19.906 11.0974 20.0368 10.8423C20.207 10.5133 20.2278 10.1506 20.0949 9.79418C19.9164 9.31337 19.4743 9.02657 18.9117 9.02657C18.7872 9.02657 18.6585 9.04133 18.5298 9.06875C18.208 9.14045 17.9029 9.25643 17.6475 9.35765C17.6289 9.36609 17.6081 9.35133 17.6102 9.33024C17.6372 8.68705 17.6683 7.82244 17.5977 7.00211C17.5355 6.25981 17.3839 5.63349 17.139 5.0873C16.892 4.53901 16.5702 4.13412 16.319 3.841C16.0803 3.56263 15.6589 3.15141 15.0237 2.78237C14.1311 2.2636 13.114 2 12.0013 2C10.8908 2 9.87572 2.2636 8.98105 2.78237C8.30849 3.1725 7.87879 3.61324 7.68367 3.841C7.4325 4.13412 7.11075 4.53901 6.86372 5.0873C6.6167 5.63349 6.46724 6.2577 6.40497 7.00211C6.33439 7.82666 6.36345 8.62168 6.39252 9.33024C6.39252 9.35133 6.37383 9.36609 6.35308 9.35765C6.09775 9.25643 5.79261 9.14045 5.47086 9.06875C5.34423 9.04133 5.21553 9.02657 5.08891 9.02657C4.52844 9.02657 4.08629 9.31337 3.9057 9.79418C3.77285 10.1506 3.7936 10.5133 3.96382 10.8423C4.09667 11.0974 4.31878 11.3336 4.64469 11.5656C4.74432 11.6373 4.91869 11.7512 5.12005 11.884C5.34631 12.0337 5.71373 12.2763 5.95037 12.4429C5.97943 12.4639 6.07907 12.5399 6.11021 12.62C6.14342 12.7043 6.14965 12.7676 6.10605 12.9363C6.0334 13.1809 5.86941 13.6238 5.52483 14.1489C5.23837 14.5896 4.88133 15.0008 4.46409 15.3762C3.94514 15.8423 3.32862 16.2535 2.63323 16.5951C2.60002 16.612 2.56057 16.631 2.51906 16.6563C2.51906 16.6563 2.1039 16.8714 2.02502 17.1497C1.90877 17.5609 2.21807 17.9469 2.53151 18.1535C3.04631 18.4909 3.67321 18.6723 4.03647 18.7714C4.13819 18.7988 4.22952 18.8241 4.31256 18.8494C4.36445 18.8663 4.49523 18.9169 4.55127 18.9907C4.62185 19.0835 4.63016 19.1974 4.65506 19.326C4.69451 19.5432 4.78377 19.811 5.04739 19.9966C5.33801 20.1991 5.70542 20.2138 6.17248 20.2328C6.6603 20.2518 7.26643 20.275 7.96183 20.5091C8.28358 20.6166 8.57627 20.8001 8.91255 21.011C9.61832 21.4517 10.4964 22 11.9951 22C13.4959 22 14.3802 21.4496 15.0901 21.0067C15.4264 20.798 15.715 20.6166 16.0305 20.5112C16.7259 20.2771 17.332 20.2539 17.8198 20.2349C18.2869 20.2159 18.6543 20.2033 18.9449 19.9987C19.2272 19.8005 19.3082 19.5053 19.3456 19.2838C19.3663 19.1742 19.3788 19.0751 19.441 18.9949C19.495 18.9253 19.6154 18.8768 19.6715 18.8579C19.7566 18.8305 19.8521 18.8051 19.9579 18.7756C20.3212 18.6765 20.7779 18.5605 21.3321 18.2421C22.0005 17.8562 22.0462 17.3838 21.9756 17.1497Z"
                                    stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round" />
                            </svg>
                            }
                            @case (SocialChannelEnum['Snapchat Story']) {
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke-width="2"
                                role="img" xmlns="http://www.w3.org/2000/svg" class="size-4">
                                <path
                                    d="M21.9756 17.1497C21.8884 16.8566 21.4712 16.6499 21.4712 16.6499C21.4317 16.6288 21.3965 16.6099 21.3674 16.5951C20.672 16.2535 20.0555 15.8423 19.5365 15.3762C19.1193 15.0008 18.7623 14.5875 18.4758 14.1489C18.125 13.6132 17.961 13.1662 17.8904 12.9237C17.851 12.7655 17.8572 12.7022 17.8904 12.62C17.9174 12.5504 17.9984 12.485 18.0357 12.4534C18.2703 12.2847 18.6481 12.0358 18.8806 11.884C19.0819 11.7512 19.2542 11.6373 19.3559 11.5656C19.6818 11.3336 19.906 11.0974 20.0368 10.8423C20.207 10.5133 20.2278 10.1506 20.0949 9.79418C19.9164 9.31337 19.4743 9.02657 18.9117 9.02657C18.7872 9.02657 18.6585 9.04133 18.5298 9.06875C18.208 9.14045 17.9029 9.25643 17.6475 9.35765C17.6289 9.36609 17.6081 9.35133 17.6102 9.33024C17.6372 8.68705 17.6683 7.82244 17.5977 7.00211C17.5355 6.25981 17.3839 5.63349 17.139 5.0873C16.892 4.53901 16.5702 4.13412 16.319 3.841C16.0803 3.56263 15.6589 3.15141 15.0237 2.78237C14.1311 2.2636 13.114 2 12.0013 2C10.8908 2 9.87572 2.2636 8.98105 2.78237C8.30849 3.1725 7.87879 3.61324 7.68367 3.841C7.4325 4.13412 7.11075 4.53901 6.86372 5.0873C6.6167 5.63349 6.46724 6.2577 6.40497 7.00211C6.33439 7.82666 6.36345 8.62168 6.39252 9.33024C6.39252 9.35133 6.37383 9.36609 6.35308 9.35765C6.09775 9.25643 5.79261 9.14045 5.47086 9.06875C5.34423 9.04133 5.21553 9.02657 5.08891 9.02657C4.52844 9.02657 4.08629 9.31337 3.9057 9.79418C3.77285 10.1506 3.7936 10.5133 3.96382 10.8423C4.09667 11.0974 4.31878 11.3336 4.64469 11.5656C4.74432 11.6373 4.91869 11.7512 5.12005 11.884C5.34631 12.0337 5.71373 12.2763 5.95037 12.4429C5.97943 12.4639 6.07907 12.5399 6.11021 12.62C6.14342 12.7043 6.14965 12.7676 6.10605 12.9363C6.0334 13.1809 5.86941 13.6238 5.52483 14.1489C5.23837 14.5896 4.88133 15.0008 4.46409 15.3762C3.94514 15.8423 3.32862 16.2535 2.63323 16.5951C2.60002 16.612 2.56057 16.631 2.51906 16.6563C2.51906 16.6563 2.1039 16.8714 2.02502 17.1497C1.90877 17.5609 2.21807 17.9469 2.53151 18.1535C3.04631 18.4909 3.67321 18.6723 4.03647 18.7714C4.13819 18.7988 4.22952 18.8241 4.31256 18.8494C4.36445 18.8663 4.49523 18.9169 4.55127 18.9907C4.62185 19.0835 4.63016 19.1974 4.65506 19.326C4.69451 19.5432 4.78377 19.811 5.04739 19.9966C5.33801 20.1991 5.70542 20.2138 6.17248 20.2328C6.6603 20.2518 7.26643 20.275 7.96183 20.5091C8.28358 20.6166 8.57627 20.8001 8.91255 21.011C9.61832 21.4517 10.4964 22 11.9951 22C13.4959 22 14.3802 21.4496 15.0901 21.0067C15.4264 20.798 15.715 20.6166 16.0305 20.5112C16.7259 20.2771 17.332 20.2539 17.8198 20.2349C18.2869 20.2159 18.6543 20.2033 18.9449 19.9987C19.2272 19.8005 19.3082 19.5053 19.3456 19.2838C19.3663 19.1742 19.3788 19.0751 19.441 18.9949C19.495 18.9253 19.6154 18.8768 19.6715 18.8579C19.7566 18.8305 19.8521 18.8051 19.9579 18.7756C20.3212 18.6765 20.7779 18.5605 21.3321 18.2421C22.0005 17.8562 22.0462 17.3838 21.9756 17.1497Z"
                                    stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round" />
                            </svg>
                            }
                            }

                            {{ getChannelName(plt) }}
                        </a>
                        }
                    </nav>
                    }
                </div>
            </div>
        </div>
    </div>

    }
    <!-- Cross Platforms - END -->

    @if (isSwitchingChannel()) {
    <atom-loading-inline title="Loading" message="Loading results..."></atom-loading-inline>
    } @else if (noResultsForChannel()) {
    <div class="w-full flex items-center justify-center p-8">
        <div class="max-w-md text-center">
            @if (isPieceBrief()) {
            <h3 class="text-base font-semibold text-primary mb-2">No results for this selection</h3>
            <p class="text-sm text-secondary">There are no live posts for the selected piece and platform. Choose
                another, or "All" to view results.</p>
            } @else {
            <h3 class="text-base font-semibold text-primary mb-2">No results for this platform</h3>
            <p class="text-sm text-secondary">There are no submitted posts for the selected platform. Select another
                platform or "All" to view results.</p>
            }
        </div>
    </div>
    } @else {

    <!-- Stats Header -->
    @if (shoutSuperSocialResultsRes() && shoutSuperSocialResultsRes()!.results) {
    <mbtr-stats-overview class="w-full" [postsList]="shoutSuperSocialResultsRes()!.results"></mbtr-stats-overview>
    } @else {
    <dl class="w-full grid grid-cols-1 gap-3 sm:grid-cols-8">
        @for (skeleton of [1, 2, 3, 4, 5, 6, 7, 8]; track skeleton) {
        <div class="overflow-hidden rounded-lg dark:bg-gray-800 bg-gray-100 px-4 py-2">
            <dt class="truncate text-sm font-medium">
                <div class="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
            </dt>
            <dd class="w-full flex flex-row justify-between items-center mt-2">
                <div class="size-5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div class="h-6 w-12 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
            </dd>
        </div>
        }
    </dl>
    }
    <!-- Stats Header - END -->

    @if (reportsEnabled && canManageReportSchedules()) {
    <mbtr-report-schedule-banner class="mt-4 block w-full" [schedule]="reportSchedule()" (preview)="previewReport()" (openSchedule)="openScheduleDialog('banner')" (viewLatest)="viewLatestReport()"></mbtr-report-schedule-banner>
    }

    <!-- Tabs Header -->
    <div
        class="flex-0 flex flex-row items-center justify-center w-full border-b border-zinc-200 dark:border-zinc-800 pb-2">

        <div class="hidden sm:block w-full">
            <nav aria-label="Tabs"
                class="isolate flex divide-x divide-gray-300 dark:divide-white/10 rounded-lg bg-gray-100 dark:bg-gray-800/50 outline -outline-offset-1 outline-gray-300 dark:outline-white/10">
                <!-- <a (click)="setResultsViewTab(0)"
                    class="group inline-flex gap-x-4 items-center justify-center relative min-w-0 flex-1 overflow-hidden rounded-l-lg px-4 py-4 text-center text-sm font-medium hover:bg-white/5 focus:z-10 hover:cursor-pointer"
                    [class]="getResultsTabClass(0)">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="lucide lucide-message-circle-icon lucide-message-circle size-4">
                        <path
                            d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719" />
                    </svg>
                    <span>Sentiment</span>
                    <span aria-hidden="true" class="absolute inset-x-0 bottom-0 h-0.5"
                        [class]="getTabBorderClass(0)"></span>
                </a> -->
                <a (click)="setResultsViewTab(1)"
                    class="group inline-flex gap-x-4 items-center justify-center relative min-w-0 flex-1 overflow-hidden px-4 py-4 text-center text-sm font-medium hover:bg-white/5 focus:z-10 hover:cursor-pointer"
                    [class]="getResultsTabClass(1)">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="lucide lucide-target-icon lucide-target size-4">
                        <circle cx="12" cy="12" r="10" />
                        <circle cx="12" cy="12" r="6" />
                        <circle cx="12" cy="12" r="2" />
                    </svg>
                    <span>Brief Accuracy</span>
                    <span aria-hidden="true" class="absolute inset-x-0 bottom-0 h-0.5"
                        [class]="getTabBorderClass(1)"></span>
                </a>
                <a (click)="setResultsViewTab(2)"
                    class="group inline-flex gap-x-4 items-center justify-center relative min-w-0 flex-1 overflow-hidden px-4 py-4 text-center text-sm font-medium hover:bg-white/5 focus:z-10 hover:cursor-pointer"
                    [class]="getResultsTabClass(2)">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="lucide lucide-trending-up-icon lucide-trending-up size-4">
                        <path d="M16 7h6v6" />
                        <path d="m22 7-8.5 8.5-5-5L2 17" />
                    </svg>
                    <span>Content Performance</span>
                    <span aria-hidden="true" class="absolute inset-x-0 bottom-0 h-0.5"
                        [class]="getTabBorderClass(2)"></span>
                </a>
                <a (click)="setResultsViewTab(3)"
                    class="group inline-flex gap-x-4 items-center justify-center relative min-w-0 flex-1 overflow-hidden rounded-r-lg px-4 py-4 text-center text-sm font-medium hover:bg-white/5 focus:z-10 hover:cursor-pointer"
                    [class]="getResultsTabClass(3)">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="lucide lucide-trophy-icon lucide-trophy size-4">
                        <path d="M10 14.66v1.626a2 2 0 0 1-.976 1.696A5 5 0 0 0 7 21.978" />
                        <path d="M14 14.66v1.626a2 2 0 0 0 .976 1.696A5 5 0 0 1 17 21.978" />
                        <path d="M18 9h1.5a1 1 0 0 0 0-5H18" />
                        <path d="M4 22h16" />
                        <path d="M6 9a6 6 0 0 0 12 0V3a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1z" />
                        <path d="M6 9H4.5a1 1 0 0 1 0-5H6" />
                    </svg>
                    <span>Top Performers</span>
                    <span aria-hidden="true" class="absolute inset-x-0 bottom-0 h-0.5"
                        [class]="getTabBorderClass(3)"></span>
                </a>
            </nav>
        </div>
    </div>
    <!-- Tabs Header - END -->

    <!-- Main Content -->
    <div class="flex min-h-0 flex-1 flex-col items-stretch justify-start w-full overflow-hidden">
        @switch (resultsViewTab()) {
        @case (0) {
        @if (submittedPosts() && submittedPosts()!.length > 0 && shoutSuperSocialResultsRes() &&
        shoutSuperSocialResultsRes()!.results) {
        <mbtr-sentiment class="min-h-0 flex-1 w-full" [focussedBrief]="focussedBrief()!"
            [submittedPosts]="submittedPosts()!" [postsList]="shoutSuperSocialResultsRes()!.results"></mbtr-sentiment>
        } @else {
        No submitted posts
        }
        }
        @case (1) {
        @if (focussedBrief()) {
        <mbtr-brief-accuracy class="min-h-0 flex-1 w-full" [focussedBrief]="focussedBrief()!"></mbtr-brief-accuracy>
        }
        }
        @case (2) {
        @if (shoutSuperSocialResultsRes() && shoutSuperSocialResultsRes()!.results && focussedBrief()) {
        <mbtr-content-performance class="min-h-0 flex-1 w-full" [postsList]="shoutSuperSocialResultsRes()!.results"
            (jumpToContent)="jumpToContent($event)"
            (downloadResultsCsv)="downloadResultsCsv()"
            (refreshResults)="refreshBriefResults()"
            [schedulePillLabel]="reportsEnabled && canManageReportSchedules() ? schedulePillLabel() : null"
            (schedulePill)="openScheduleDialog('pill')"></mbtr-content-performance>
        }
        }
        @case (3) {
        @if (shoutSuperSocialResultsRes() && shoutSuperSocialResultsRes()!.results) {
        <mbtr-top-performers class="min-h-0 flex-1 w-full" [postsList]="shoutSuperSocialResultsRes()!.results"
            (jumpToContent)="jumpToContent($event)"></mbtr-top-performers>
        }
        }
        }
    </div>
    <!-- Main Content - END -->
    }
    } @else {
    No brief selected
    }
    }
</div>


@if (videoId() && creator()) {
<vs-video-feedback [videoId]="videoId()" [creator]="creator()!" [isUgcOnly]="isUgcOnly()" [briefId]="briefId()!"
    (close)="closeVideoFeedback()"></vs-video-feedback>
}

@if (scheduleDialog(); as dialog) {
<mbtr-report-schedule-dialog [briefId]="dialog.briefId" [briefName]="dialog.briefName" [schedule]="dialog.schedule"
    [currentUserEmail]="dialog.currentUserEmail" (closed)="onScheduleDialogClosed($event)"></mbtr-report-schedule-dialog>
}